import React, { useMemo, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Component as ComponentIcon, CheckCircle2, Hash, Image, Ruler, PieChart, Grid3x3, ArrowLeftRight, Table, Lightbulb, Coins, Columns, Clock, Grid2x2, BookOpen, Scissors, SquareStack, Circle, Crosshair, Shapes, BoxIcon, Search, BarChart3, Percent, GitBranch, Map, Compass, LayoutGrid, Gauge, Globe, Thermometer, Users, FileText, Calendar, TrendingUp, Timer, EyeOff } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Question } from '../../../../content';
import { VISUAL_REGISTRY } from '../../../../visuals';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
// Color map for module badges
const MODULE_BADGE_COLORS: Record<string, {
  dot: string;
}> = {};
Object.entries(ADMIN_MODULE_META).forEach(([code, meta]) => {
  MODULE_BADGE_COLORS[code] = {
    dot: meta.color
  };
});
const VISUAL_ICONS: Record<string, React.ElementType> = {
  stairs: Ruler,
  fraction: PieChart,
  geo: Grid3x3,
  numberline: ArrowLeftRight,
  ratio: Table,
  rule: Lightbulb,
  money: Coins,
  column: Columns,
  clock: Clock,
  decimalgrid: Grid2x2,
  verbTable: BookOpen,
  kofschipMnemonic: Scissors,
  syllableBlocks: SquareStack,
  circle: Circle,
  coordinateGrid: Crosshair,
  box3d: BoxIcon,
  compositeShape: Shapes,
  geometricShape: Shapes,
  barChart: BarChart3,
  percentageBar: Percent,
  fractionStrip: Columns,
  splitDiagram: GitBranch,
  sentenceDiagram: GitBranch,
  mapScale: Map,
  protractor: Compass,
  symmetryGrid: Grid3x3,
  numberBoard: LayoutGrid,
  speedDiagram: Gauge,
  priceCompare: BarChart3,
  timeline: Clock,
  worldClockPanel: Globe,
  thermometer: Thermometer,
  ruler: Ruler,
  measuringCup: BarChart3,
  weighScale: BarChart3,
  equalSharing: Users,
  readingPassage: FileText,
  calendar: Calendar,
  lineChart: TrendingUp,
  digitalClock: Timer
};
const VISUAL_DESCRIPTIONS: Record<string, string> = {
  stairs: 'Metriek stelsel trap voor omrekenen',
  fraction: 'Breuken cirkel of rechthoek',
  geo: 'Geometrisch raster voor oppervlakte/omtrek',
  numberline: 'Getallenlijn voor positioneren en sprongen',
  ratio: 'Verhoudingstabel',
  rule: 'Regelkaart met uitleg',
  money: 'Geldmunten en biljetten',
  column: 'Cijferend rekenen kolommen',
  clock: 'Analoge klok',
  decimalgrid: 'Kommagetallen raster',
  verbTable: 'Werkwoordspelling tabel',
  kofschipMnemonic: "'t Kofschip ezelsbruggetje",
  syllableBlocks: 'Lettergrepen blokken',
  circle: 'Cirkel met straal/diameter',
  coordinateGrid: 'Assenstelsel met coördinaten',
  box3d: '3D kubus of balk',
  compositeShape: 'Samengestelde 2D vorm',
  geometricShape: 'Basis 2D vorm',
  barChart: 'Staafdiagram',
  percentageBar: 'Percentagebalk (0-100%)',
  fractionStrip: 'Breukenstrook',
  splitDiagram: 'Splitsingsschema',
  sentenceDiagram: 'Zinsontleding schema',
  mapScale: 'Kaartschaal',
  protractor: 'Geodriehoek / gradenboog',
  symmetryGrid: 'Symmetrie raster',
  numberBoard: 'Honderdveld / getallenbord',
  speedDiagram: 'Snelheidsmeter / diagram',
  priceCompare: 'Prijsvergelijking weegschaal',
  timeline: 'Tijdlijn',
  worldClockPanel: 'Wereldklokken paneel',
  thermometer: 'Thermometer',
  ruler: 'Liniaal',
  measuringCup: 'Maatbeker',
  weighScale: 'Weegschaal',
  equalSharing: 'Eerlijk verdelen (poppetjes)',
  readingPassage: 'Leestekst',
  calendar: 'Kalender',
  lineChart: 'Lijngrafiek',
  digitalClock: 'Digitale klok'
};
interface VisualsTabProps {
  questions: Question[];
  onSelectVisual: (visualType: string) => void;
}
export function VisualsTab({
  questions,
  onSelectVisual
}: VisualsTabProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const registeredVisuals = useMemo(() => Object.keys(VISUAL_REGISTRY).sort(), []);
  // Build usage stats for each visual
  const visualUsage = useMemo(() => {
    const usage: Record<string, {
      count: number;
      stimulusCount: number;
      supportCount: number;
      families: Set<string>;
      modules: Set<string>;
      questions: Question[];
    }> = {};
    registeredVisuals.forEach(v => {
      usage[v] = {
        count: 0,
        stimulusCount: 0,
        supportCount: 0,
        families: new Set(),
        modules: new Set(),
        questions: []
      };
    });
    questions.forEach(q => {
      const moduleCode = q.familyId?.split('-')[0] || 'UNK';
      const usedTypes = new Set<string>();
      q.stimulusAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].stimulusCount++;
          usage[aid.type].families.add(q.familyId || '');
          usage[aid.type].modules.add(moduleCode);
          usedTypes.add(aid.type);
        }
      });
      q.supportAids?.forEach(aid => {
        if (aid.type && usage[aid.type]) {
          usage[aid.type].count++;
          usage[aid.type].supportCount++;
          usage[aid.type].families.add(q.familyId || '');
          usage[aid.type].modules.add(moduleCode);
          usedTypes.add(aid.type);
        }
      });
      usedTypes.forEach(type => {
        if (usage[type]) usage[type].questions.push(q);
      });
    });
    return usage;
  }, [registeredVisuals, questions]);
  // Sort by usage
  const sortedVisuals = useMemo(() => [...registeredVisuals].sort((a, b) => visualUsage[b].count - visualUsage[a].count), [registeredVisuals, visualUsage]);
  // Filter by search
  const filteredVisuals = useMemo(() => {
    if (!searchQuery.trim()) return sortedVisuals;
    const query = searchQuery.toLowerCase();
    return sortedVisuals.filter(v => v.toLowerCase().includes(query));
  }, [sortedVisuals, searchQuery]);
  // Stats
  const activeVisuals = sortedVisuals.filter(v => visualUsage[v].count > 0).length;
  const unusedVisuals = registeredVisuals.length - activeVisuals;
  const totalReferences = Object.values(visualUsage).reduce((sum, u) => sum + u.count, 0);
  return <div className="space-y-6" data-id="element-4920">
      {/* Stats Row */}
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-4921">
        <motion.div variants={itemVariants} data-id="element-4922">
          <StatCard label="Geregistreerde Visuals" value={registeredVisuals.length} icon={<ComponentIcon className="w-5 h-5" data-id="element-4924" />} color="purple" data-id="element-4923" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4925">
          <StatCard label="Actief in Gebruik" value={activeVisuals} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4927" />} color="emerald" data-id="element-4926" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4928">
          <StatCard label="Ongebruikte Visuals" value={unusedVisuals} icon={<EyeOff className="w-5 h-5" data-id="element-4930" />} color="gray" data-id="element-4929" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-4931">
          <StatCard label="Totale Referenties" value={totalReferences.toLocaleString('nl-NL')} icon={<Hash className="w-5 h-5" data-id="element-4933" />} color="blue" data-id="element-4932" />
        </motion.div>
      </motion.div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4934">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4935">
          <div data-id="element-4936">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-4937">
              <ComponentIcon className="w-5 h-5 text-purple-500" data-id="element-4938" />
              Visuals Overzicht
            </h3>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-4939">
              {activeVisuals} van {registeredVisuals.length} visuals actief in
              gebruik
            </p>
          </div>
          <div className="w-full sm:w-64" data-id="element-4940">
            <Input type="text" placeholder="Zoek visual..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-4942" />} variant="admin" size="sm" data-id="element-4941" />
          </div>
        </div>

        {/* Visual Cards Grid */}
        <div className="p-4 sm:p-5" data-id="element-4943">
          {filteredVisuals.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-4944">
              Geen visuals gevonden voor "{searchQuery}"
            </div> : <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-4945">
              {filteredVisuals.map(v => {
            const usage = visualUsage[v];
            const isUsed = usage.count > 0;
            const Icon = VISUAL_ICONS[v] || Image;
            const description = VISUAL_DESCRIPTIONS[v] || 'Geen beschrijving beschikbaar';
            return <motion.div key={v} variants={itemVariants} data-id="element-4946">
                    <Card className={`p-5 h-full flex flex-col transition-all duration-200 cursor-pointer group relative ${isUsed ? 'hover:shadow-md hover:border-purple-300 border-gray-200' : 'opacity-50 border-gray-100 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isUsed && onSelectVisual(v)} title={description} data-id="element-4947">
                      <div className="flex items-start justify-between mb-4" data-id="element-4948">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isUsed ? 'bg-purple-100 text-purple-600 group-hover:scale-105 transition-transform' : 'bg-gray-200 text-gray-400'}`} data-id="element-4949">
                          <Icon className="w-5 h-5" data-id="element-4950" />
                        </div>
                        <div className="text-right" data-id="element-4951">
                          <div className={`text-xl font-black ${isUsed ? 'text-purple-600' : 'text-gray-400'}`} data-id="element-4952">
                            {usage.count}
                          </div>
                          <div className="text-[10px] text-gray-500 font-medium" data-id="element-4953">
                            referenties
                          </div>
                        </div>
                      </div>

                      <div className="mb-4" data-id="element-4954">
                        <code className="text-sm font-mono font-bold text-gray-900 block mb-1" data-id="element-4955">
                          {v}
                        </code>
                        <p className="text-xs text-gray-500 mb-3 line-clamp-1" data-id="element-4956">
                          {description}
                        </p>
                        {isUsed && <div className="space-y-1.5" data-id="element-4957">
                            <div className="flex justify-between text-[10px] text-gray-500" data-id="element-4958">
                              <span data-id="element-4959">Stimulus ({usage.stimulusCount})</span>
                              <span data-id="element-4960">Support ({usage.supportCount})</span>
                            </div>
                            <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden flex" data-id="element-4961">
                              <div className="bg-blue-400 h-full" style={{
                        width: `${usage.stimulusCount / usage.count * 100}%`
                      }} data-id="element-4962" />
                              <div className="bg-amber-400 h-full" style={{
                        width: `${usage.supportCount / usage.count * 100}%`
                      }} data-id="element-4963" />
                            </div>
                          </div>}
                      </div>

                      <div className="mt-auto pt-3 border-t border-gray-100" data-id="element-4964">
                        <div className="flex flex-wrap gap-1.5" data-id="element-4965">
                          {usage.modules.size > 0 ? Array.from(usage.modules).sort().slice(0, 4).map(m => <ModuleBadge key={m} code={m} data-id="element-4966" />) : <span className="text-xs text-gray-400 italic" data-id="element-4967">
                              Niet in gebruik
                            </span>}
                          {usage.modules.size > 4 && <span className="text-[10px] text-gray-400 font-medium px-1.5 py-0.5" data-id="element-4968">
                              +{usage.modules.size - 4}
                            </span>}
                        </div>
                      </div>
                    </Card>
                  </motion.div>;
          })}
            </motion.div>}
        </div>
      </div>
    </div>;
}
function ModuleBadge({
  code
}: {
  code: string;
}) {
  const colors = MODULE_BADGE_COLORS[code] || {
    dot: 'bg-gray-500'
  };
  return <span className="text-[10px] font-bold px-2 py-1 rounded-md border bg-gray-50 border-gray-200 text-gray-700 inline-flex items-center gap-1" title={code} data-id="element-4969">
      <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} data-id="element-4970" />
      {code}
    </span>;
}